import { LotteryResult, GeneratedNumbers, UserWinningStats } from '../types/lottery'
import { apiClient } from './apiClient'

class ApiService {

  // Public API methods (no authentication required)
  
  async getLatestLotteryResult(): Promise<LotteryResult> {
    const response = await apiClient.get('/api/v1/public/lottery/latest-result')
    return response.data
  }

  async getLotteryResults(skip = 0, limit = 10): Promise<{
    items: LotteryResult[]
    total: number
    skip: number
    limit: number
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/results', {
      params: { skip, limit }
    })
    return response.data
  }

  async getLotteryResultByRound(round: number): Promise<LotteryResult> {
    const response = await apiClient.get(`/api/v1/public/lottery/results/${round}`)
    return response.data
  }

  async getLotteryStatistics(): Promise<{
    hot_numbers: Array<{ number: number; frequency: number }>
    cold_numbers: Array<{ number: number; frequency: number }>
    number_frequencies: { [key: number]: number }
    total_rounds: number
    last_updated: string
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/statistics')
    return response.data
  }

  async getNumberFrequencyAnalysis(rounds = 50): Promise<{
    number_frequencies: { [key: number]: number }
    hot_numbers: Array<{ number: number; frequency: number }>
    cold_numbers: Array<{ number: number; frequency: number }>
    total_rounds: number
    analysis_period: string
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/statistics/frequency', {
      params: { rounds }
    })
    return response.data
  }

  async getNumberPatterns(): Promise<{
    odd_even_distribution: {
      odd_count: number
      even_count: number
      percentage: { odd: number; even: number }
    }
    number_sum_ranges: {
      low: number
      medium: number
      high: number
      percentages: { low: number; medium: number; high: number }
    }
    consecutive_patterns: {
      none: number
      pairs: number
      triplets: number
      more: number
      percentages: { none: number; pairs: number; triplets: number; more: number }
    }
    total_rounds_analyzed: number
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/statistics/patterns')
    return response.data
  }

  async getWinningCombinations(): Promise<{
    number_pairs: Array<{
      pair: [number, number]
      frequency: number
      percentage: number
    }>
    number_triplets: Array<{
      triplet: [number, number, number]
      frequency: number
      percentage: number
    }>
    total_combinations: number
    analysis_period: string
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/statistics/winning-combinations')
    return response.data
  }

  async getCustomerWinningStats(): Promise<{
    round_number: number | null
    draw_date: string | null
    by_rank: Record<string, { winners: number; prize_amount: number }>
    total_winners: number
    total_prize_amount: number
  }> {
    const response = await apiClient.get('/api/v1/public/lottery/customer-winning-stats')
    return response.data
  }

  async getPublicSubscriptionTiers(): Promise<Array<{
    id: string
    name: string
    price: number
    max_numbers_per_month: number
    features: {
      premium_support: boolean
      advanced_analytics: boolean
      priority_generation: boolean
      exclusive_algorithms: boolean
    }
    description: string
    is_active: boolean
  }>> {
    const response = await apiClient.get('/api/v1/public/subscription-tiers')
    return response.data
  }

  async getActiveAnnouncements(): Promise<Array<{
    id: string
    title: string
    content: string
    type: 'popup' | 'banner' | 'modal'
    priority: 'low' | 'medium' | 'high'
    is_active: boolean
    start_date: string
    end_date: string
    target_audience: string[]
    created_at: string
  }>> {
    const response = await apiClient.get('/api/v1/public/announcements')
    return response.data
  }

  // Authentication API methods

  async register(data: {
    email: string
    name: string
    mobile_number: string
    password: string
  }): Promise<{
    user: any
  }> {
    const response = await apiClient.post('/api/v1/auth/register', data)
    return response.data
  }

  async login(data: {
    username: string
    password: string
  }): Promise<{
    user: any
  }> {
    const response = await apiClient.post('/api/v1/auth/login', data)
    return response.data
  }

  async refreshAccessToken(): Promise<{ ok: boolean }> {
    const response = await apiClient.post('/api/v1/auth/refresh')
    return response.data
  }

  async getCurrentUser(): Promise<any> {
    const response = await apiClient.get('/api/v1/auth/me')
    return response.data
  }

  async updateProfile(data: {
    name?: string
    mobile_number?: string
  }): Promise<any> {
    const response = await apiClient.put('/api/v1/auth/me/profile', data)
    return response.data
  }

  async getCurrentUserSubscription(): Promise<{
    has_subscription: boolean
    subscription?: any
    message?: string
  }> {
    const response = await apiClient.get('/api/v1/auth/me/subscription')
    return response.data
  }

  async getCurrentUserGeneratedNumbers(skip = 0, limit = 20): Promise<{
    generated_numbers: GeneratedNumbers[]
    total: number
    skip: number
    limit: number
  }> {
    const response = await apiClient.get('/api/v1/auth/me/generated-numbers', {
      params: { skip, limit }
    })
    return response.data
  }

  async getCurrentUserWinningStats(): Promise<UserWinningStats> {
    const response = await apiClient.get('/api/v1/auth/me/winning-stats')
    return response.data
  }

  async logout(): Promise<void> {
    await apiClient.post('/api/v1/auth/logout')
  }

  // Google OAuth URLs
  async getGoogleLoginUrl(): Promise<{ login_url: string }> {
    const response = await apiClient.get('/api/v1/auth/google/login/url')
    return response.data
  }

  // Kakao OAuth URLs
  async getKakaoLoginUrl(): Promise<{ login_url: string }> {
    const response = await apiClient.get('/api/v1/auth/kakao/login/url')
    return response.data
  }

  // Health check
  async checkHealth(): Promise<{ status: string }> {
    const response = await apiClient.get('/api/v1/public/health')
    return response.data
  }

  // Admin/Manager endpoints (authenticated)
  async generateLotteryNumbers(data: {
    user_id: string
    count?: number
    round_target?: number
  }): Promise<{
    user_id: string
    numbers_sets: number[][]
    round_target?: number
    generated_at: string
  }> {
    const response = await apiClient.post('/api/v1/lottery/generate', data)
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService